import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection, query, where, getDocs, getDoc,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';

export const MAX_MEMBERS_PER_GROUP = 3; // フォーマンセル：リーダー1名＋メンバー3名 = 4名/グループ

// ── チーム全体を取得するhook ──
export function useTeam() {
  const { user } = useAuth();
  const [team, setTeam]       = useState(null);
  const [groups, setGroups]   = useState([]); // [{ id, name, leaderId, leaderName, members: [] }]
  const [myRole, setMyRole]   = useState(null); // 'owner' | 'leader' | 'member'
  const [myGroupId, setMyGroupId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const isOwner  = myRole === 'owner';
  const isLeader = myRole === 'leader';
  const canManage = isOwner || isLeader;

  // グループ一覧＋各メンバーを取得
  const fetchGroups = useCallback(async (teamId) => {
    const groupsSnap = await getDocs(collection(db, `teams/${teamId}/groups`));
    const groupList = await Promise.all(groupsSnap.docs.map(async (gDoc) => {
      const gData = { id: gDoc.id, ...gDoc.data() };
      const membersSnap = await getDocs(collection(db, `teams/${teamId}/groups/${gDoc.id}/members`));
      const members = await Promise.all(membersSnap.docs.map(async (mDoc) => {
        const mData = { id: mDoc.id, ...mDoc.data() };
        try {
          const uDoc = await getDoc(doc(db, 'users', mData.userId));
          const p = uDoc.exists() ? uDoc.data() : {};
          return { ...mData, displayName: p.displayName || p.username || '未設定', email: p.email || '', photoURL: p.photoURL || null };
        } catch { return mData; }
      }));
      return { ...gData, members };
    }));
    setGroups(groupList);
    return groupList;
  }, []);

  const fetchTeam = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true); setError(null);

      // オーナーとして検索
      const qOwner = query(collection(db, 'teams'), where('ownerId', '==', user.uid));
      const ownerSnap = await getDocs(qOwner);
      if (!ownerSnap.empty) {
        const tDoc = ownerSnap.docs[0];
        const foundTeam = { id: tDoc.id, ...tDoc.data() };
        setTeam(foundTeam); setMyRole('owner'); setMyGroupId(null);
        await fetchGroups(foundTeam.id);
        return;
      }

      // グループメンバー・リーダーとして検索
      const allTeamsSnap = await getDocs(collection(db, 'teams'));
      for (const tDoc of allTeamsSnap.docs) {
        const groupsSnap = await getDocs(collection(db, `teams/${tDoc.id}/groups`));
        for (const gDoc of groupsSnap.docs) {
          const qMember = query(
            collection(db, `teams/${tDoc.id}/groups/${gDoc.id}/members`),
            where('userId', '==', user.uid)
          );
          const mSnap = await getDocs(qMember);
          if (!mSnap.empty) {
            const foundTeam = { id: tDoc.id, ...tDoc.data() };
            const role = mSnap.docs[0].data().role || 'member';
            setTeam(foundTeam); setMyRole(role); setMyGroupId(gDoc.id);
            await fetchGroups(foundTeam.id);
            return;
          }
        }
      }

      setTeam(null); setMyRole(null); setGroups([]); setMyGroupId(null);
    } catch (err) {
      console.error('Team fetch error:', err); setError(err);
    } finally { setLoading(false); }
  }, [user, fetchGroups]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  // チーム作成（オーナー）
  const createTeam = async (teamName) => {
    const teamRef = await addDoc(collection(db, 'teams'), {
      name: teamName, ownerId: user.uid,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    setTeam({ id: teamRef.id, name: teamName, ownerId: user.uid });
    setMyRole('owner'); setGroups([]);
    return teamRef.id;
  };

  // グループ作成（オーナーのみ）
  const createGroup = async (groupName) => {
    if (!isOwner || !team) throw new Error('オーナー権限が必要です');
    const gRef = await addDoc(collection(db, `teams/${team.id}/groups`), {
      name: groupName, leaderId: null, leaderName: null,
      createdAt: serverTimestamp(),
    });
    await fetchGroups(team.id);
    return gRef.id;
  };

  // グループ名変更（オーナーのみ）
  const updateGroupName = async (groupId, newName) => {
    if (!isOwner || !team) throw new Error('オーナー権限が必要です');
    await updateDoc(doc(db, `teams/${team.id}/groups`, groupId), { name: newName });
    await fetchGroups(team.id);
  };

  // グループ削除（オーナーのみ）
  const deleteGroup = async (groupId) => {
    if (!isOwner || !team) throw new Error('オーナー権限が必要です');
    const group = groups.find(g => g.id === groupId);
    if (group?.members?.length > 0) throw new Error('メンバーを先に削除してください');
    await deleteDoc(doc(db, `teams/${team.id}/groups`, groupId));
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };

  // 招待トークン生成
  const inviteMember = async (email, role, groupId, groupName) => {
    if (!team) throw new Error('チームが見つかりません');
    if (!groupId) throw new Error('グループIDが指定されていません');
    const group = groups.find(g => g.id === groupId);
    const resolvedGroupName = group?.name || groupName || 'グループ';

    // リーダー上限チェック（1グループ1リーダー）
    if (role === 'leader') {
      if (group.leaderId) throw new Error('このグループにはすでにリーダーがいます');
    }
    // メンバー上限チェック
    if (role === 'member') {
      const memberCount = group.members.filter(m => m.role === 'member').length;
      if (memberCount >= MAX_MEMBERS_PER_GROUP) throw new Error('1グループのメンバーは3名までです（リーダー1名＋メンバー3名のフォーマンセル）');
    }

    const token = crypto.randomUUID();
    const expiresAt = new Date(); expiresAt.setDate(expiresAt.getDate() + 7);
    await addDoc(collection(db, 'team_invites'), {
      teamId: team.id, teamName: team.name,
      groupId, groupName: resolvedGroupName,
      email, role, token, status: 'pending', expiresAt,
      inviterName: user.displayName || 'チーム管理者', inviterId: user.uid,
      createdAt: serverTimestamp(),
    });
    return token;
  };

  // メンバー削除
  const removeMember = async (groupId, memberDocId, targetUserId) => {
    if (!canManage) throw new Error('権限がありません');
    if (isLeader && groupId !== myGroupId) throw new Error('自分のグループのみ操作できます');
    if (targetUserId === team?.ownerId) throw new Error('オーナーは削除できません');
    await deleteDoc(doc(db, `teams/${team.id}/groups/${groupId}/members`, memberDocId));
    // リーダー削除の場合はgroupのleaderIdもリセット
    const group = groups.find(g => g.id === groupId);
    const member = group?.members.find(m => m.id === memberDocId);
    if (member?.role === 'leader') {
      await updateDoc(doc(db, `teams/${team.id}/groups`, groupId), { leaderId: null, leaderName: null });
    }
    await fetchGroups(team.id);
  };

  // 相談送信（リーダー→オーナー）
  const sendConsultation = async (title, message) => {
    if (!team || !isLeader) throw new Error('リーダーのみ相談を送信できます');
    const myGroup = groups.find(g => g.id === myGroupId);
    await addDoc(collection(db, 'team_consultations'), {
      teamId: team.id, teamName: team.name,
      groupId: myGroupId, groupName: myGroup?.name || '',
      fromUserId: user.uid, fromName: user.displayName || 'リーダー',
      toUserId: team.ownerId, title, message,
      status: 'pending', createdAt: serverTimestamp(),
    });
  };

  const fetchConsultations = async () => {
    if (!team) return [];
    const q = query(collection(db, 'team_consultations'), where('teamId', '==', team.id));
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ta = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const tb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return tb - ta;
      });
  };

  const replyConsultation = async (consultId, reply) => {
    if (!isOwner) throw new Error('オーナーのみ返信できます');
    await updateDoc(doc(db, 'team_consultations', consultId), {
      reply, repliedAt: serverTimestamp(), status: 'replied', repliedBy: user.uid,
    });
  };

  // 自分のグループ（リーダー用）
  const myGroup = groups.find(g => g.id === myGroupId) || null;

  return {
    team, groups, myGroup, myRole, myGroupId, loading, error,
    isOwner, isLeader, canManage,
    createTeam, createGroup, updateGroupName, deleteGroup,
    inviteMember, removeMember,
    sendConsultation, fetchConsultations, replyConsultation,
    refetch: fetchTeam,
  };
}
